"use client";

import { Box, Grow, alpha, styled, Typography } from "@mui/material";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import SettingsIcon from "@mui/icons-material/Settings";
import BlockIcon from "@mui/icons-material/Block";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import useCVUpload from "@lib/candidates/useCVUpload";
import { useJobStatus } from "@lib/jobs/useJobStatus";
import slugify from "@lib/helpers/slugify";
import { JobStatus, FILE_DRAG_STATE } from "@lib/enums";
import config from "@lib/config";

const DropZone = styled(Box, {
  shouldForwardProp: (prop) => prop !== "dragging",
})(({ dragging, theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  width: "100%",
  padding: "40px",
  marginTop: "24px",
  border: `1px dotted ${theme.palette.primary.main}`,
  borderRadius: "16px",
  backgroundColor: dragging ? alpha(theme.palette.primary.main, 0.06) : "rgba(255,255,255,0.8)",
  transition: "background-color 0.2s ease, border-color 0.2s ease",
  cursor: "pointer",
  gap: "12px",
}));

const IconContainer = styled(Box)({
  position: "relative",
  width: "40px",
  height: "40px",

  "& > *": {
    position: "absolute !important",
    top: 0,
    left: 0,
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  "& svg": {
    fontSize: "40px",
  },

  "& img": {
    width: "40px",
    height: "40px",
    objectFit: "contain",
  },
});

const addQueuedToStatusBar = (files, addJob) => {
  files.forEach((file) => {
    addJob(slugify(file.name), { status: JobStatus.QUEUED });
  });
};

export default function CVUpload({ onUploadComplete }) {
  const [state, setState] = useState(FILE_DRAG_STATE.initial);
  const [queue, setQueue] = useState([]);
  const { addJob } = useJobStatus();
  const upload = useCVUpload();
  const inFlightRef = useRef(false);

  const onDropAccepted = useCallback(
    (files) => {
      if (inFlightRef.current || queue.length > 0) return;
      setState(FILE_DRAG_STATE.processing);
      addQueuedToStatusBar(files, addJob);
      setQueue(files);
    },
    [addJob, queue.length]
  );

  const onDropRejected = useCallback(() => {
    setState(FILE_DRAG_STATE.rejected);
    setTimeout(() => {
      setState(FILE_DRAG_STATE.initial);
    }, 5000);
  }, []);

  const accept = {
    "application/pdf": [".pdf"],
    "application/msword": [".doc", ".docx"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
      ".docx",
    ],
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    maxFiles: config.maxFileUpload,
    maxSize: config.maxFileSize,
    onDropAccepted,
    onDropRejected,
    accept,
    disabled: state === FILE_DRAG_STATE.processing,
    noClick: true,
  });

  // Sync drag-active into local state during render (React's replacement for a
  // set-state-in-effect). Only transition to "dragging" on the rising edge of
  // isDragActive, matching the previous effect's behavior.
  const [prevDragActive, setPrevDragActive] = useState(isDragActive);
  if (isDragActive !== prevDragActive) {
    setPrevDragActive(isDragActive);
    if (isDragActive) {
      setState(FILE_DRAG_STATE.dragging);
    } else if (state === FILE_DRAG_STATE.dragging) {
      setState(FILE_DRAG_STATE.initial);
    }
  }

  useEffect(() => {
    if (queue.length === 0 || inFlightRef.current) return;
    const file = queue[0];
    inFlightRef.current = true;

    upload.mutate(
      { file, fileId: slugify(file.name) },
      {
        onSuccess: (data) => {
          inFlightRef.current = false;
          setQueue((prev) => prev.slice(1));
          if (queue.length === 1) {
            if (typeof gtag !== "undefined") {
              gtag("event", "upload_cv", {
                app_name: "Floats",
                screen_name: "Candidates",
              });
            }
            setState(FILE_DRAG_STATE.initial);
            onUploadComplete?.();
          }
        },
        onError: () => {
          inFlightRef.current = false;
          setState(FILE_DRAG_STATE.initial);
          console.error("CV upload failed");
          setQueue((prev) => prev.slice(1));
          onUploadComplete?.();
        },
      }
    );
  }, [queue]);

  const descriptionContent = () => {
    if (state === FILE_DRAG_STATE.dragging) return "You got this! Drop it!";
    if (state === FILE_DRAG_STATE.processing) return "Our AI is processing the CV…";
    if (state === FILE_DRAG_STATE.rejected)
      return `Only PDF or DOC files accepted. Max ${config.maxFileUpload} files, 6 MB each.`;
    return (
      <>
        Drag and drop CV files or
        <br />
        <Box
          component="button"
          onClick={(e) => { e.stopPropagation(); open(); }}
          sx={{
            background: "none",
            border: "none",
            padding: 0,
            color: "primary.main",
            fontFamily: "inherit",
            fontSize: "inherit",
            fontWeight: 400,
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          browse files
        </Box>
      </>
    );
  };

  return (
    <DropZone dragging={state === FILE_DRAG_STATE.dragging} {...getRootProps()}>
      <IconContainer>
        <Grow in={state === FILE_DRAG_STATE.initial} timeout={500}>
          <img src="/img/floats/cloud-upload.png" alt="" />
        </Grow>
        <Grow in={state === FILE_DRAG_STATE.dragging} timeout={500}>
          <FileCopyIcon sx={{ fontSize: "40px" }} color="primary" />
        </Grow>
        <Grow in={state === FILE_DRAG_STATE.rejected} timeout={500}>
          <BlockIcon sx={{ fontSize: "40px" }} color="primary" />
        </Grow>
        <Grow
          in={state === FILE_DRAG_STATE.processing}
          timeout={500}
          sx={{
            animation: "spin 2s linear infinite",
            "@keyframes spin": {
              "0%": { transform: "rotate(360deg)" },
              "100%": { transform: "rotate(0deg)" },
            },
          }}
        >
          <SettingsIcon sx={{ fontSize: "40px" }} color="primary" />
        </Grow>
      </IconContainer>

      <input {...getInputProps()} />

      <Typography
        variant="body2"
        sx={{ color: "text.secondary", fontWeight: 300, lineHeight: 1.6 }}
      >
        {descriptionContent()}
      </Typography>
    </DropZone>
  );
}
